/**
 * Created by Yoyo Fang on 4/19/2019.
 * Description:
  * shows list of entities and selected entity detail
 * can be used at page level awa component level
 * ------ maintenance history ------
 * 06/06/2019   Yoyo Fang   when reload triggered from external ,silently set text on keyword search
 * 06/18/2019   Yoyo Fang   integrade with smart reducer
 * 06/20/2019   Yoyo Fang   selected entity id is not changed, do not reload. especially after first item selected and updated.
 * 09/05/2019   Ella Ma upgrade ag-grid-angular
 * 12/01/2020   Ella Ma Fix When the grid drop-down scrolls to the next page, the grid scrolling position remains unchanged [TAM-30314]
 * Updated by Daniel on 1/11/2022: Combined the entity-list.base component with entity-list component. because input parameters in the extends component can't be passed in build mode when upgradint to angular10 from angular8.
 */
import { Component, OnInit, OnDestroy, HostListener, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import { GridOptions, ColDef } from 'ag-grid-community';
import { ValueGetterParams } from 'ag-grid-community/dist/lib/entities/colDef';

import { smartSelector } from '../../redux';
import { ScreenActionTypes } from '../../redux/actions/screen.actions';
import { lastCustomActionSelector } from '../../redux/reducers/action-history.reducer';

import { EntityListAction, EntityListActionType, EntityListConfig, EntityListFeedBack, EntityListFeedbackType } from '../../tamalelibs/components/entity-list.config';
import { EntitiesViewConstants } from '../../tamalelibs/models/smart-reducer.model';
import { EDIT_ENTITY_SUCCESS } from '../../tamalelibs/redux/entity.actions';
import { EntityList } from '../../tamalelibs/models/entity-list.model';
import { RELOAD_ENTITIES } from '../../tamalelibs/redux/entity-list.actions';

import { ResearchView, businessConstants } from '../../constants/business.constants';
import { ResearchLayoutConfig } from '../../components/research-layout/research-layout.data';
import { EntityConfig, EntityAction, EntityActionTypes, EntityFeedbackAction, EntityFeedbackTypes, SourceType } from '../entity/entity.model';
import { KeywordSearchConfig } from '../../widgets/keyword-search/keyword-search.component';
import { DEFAULT_PAGE_SIZE } from '../../widgets/grid/grid.model';
import { NotificationStyles, NotificationOptions } from '../../widgets/notification/notification.model';
import { ToastService } from '../../widgets/toast/toast.service';
import { StringLiteralsPipe } from '../../pipes/translate.pipe';
import { ContextMenuConfig, ContextMenuShowAction } from '../../widgets/tam-context-menu/tam-context-menu.model';
import * as _lodash from 'lodash';
import { GridCustomColumnFeedbackTypes } from '../../widgets/ag-grid/custom-action-column.model';
import { CustomActionColumnComponent } from '../../widgets/ag-grid/custom-action-column.component';
import { StoreQuerierService } from '../../services/store-querier.service';

const INIT = 'init';
@Component({
    selector: 'tam-entity-list',
    templateUrl: './entity-list.component.html',
    styleUrls: ['./entity-list.component.scss']
})
export class EntityListComponent implements OnInit, OnDestroy {
    @Input() config: EntityListConfig;

    columnDefs: Array<ColDef> =
        [
            {
                headerName: '',
                valueGetter: (params: ValueGetterParams) => params.data && params.data.isPublic || null,
                headerClass: 'icon-header-style',
                colId: 'privacy',
                menuTabs: [],
                minWidth: 40,
                maxWidth: 40,
                width: 40,
                resizable: false,
                suppressMenu: true,
                suppressMovable: true,
                cellStyle: {
                    'text-align': 'center'
                },
                cellRenderer: (params) => {
                    if (!params || !params.data || !params.data.isPublic) {
                        const divElement = document.createElement('div');
                        divElement.setAttribute('style', 'background: url("./assets/images/singlesvg/locked.svg") no-repeat center/contain;'
                            + '-webkit-background-size: contain;'
                            + '-moz-background-size: contain;'
                            + '-o-background-size: contain;'
                            + 'background-size: contain;'
                            + 'height: 16px; width: 16px; margin-top: 4px;');
                        return divElement;
                    }
                    return null;
                }
            },
            {
                headerName: 'Short Name',
                valueGetter: (params: ValueGetterParams) => params.data && params.data.shortName || '',
                tooltipValueGetter: this._tooltip,
                colId: 'shortName',
                suppressMenu: true,
                suppressMovable: true
            },
            {
                headerName: 'Name',
                valueGetter: (params: ValueGetterParams) => params.data && params.data.name || '',
                tooltipValueGetter: this._tooltip,
                colId: 'name',
                suppressMenu: true,
                suppressMovable: true
            },
            {
                headerName: 'Entity Type',
                valueGetter: (params: ValueGetterParams) => params.data && params.data.type && params.data.type.name || '',
                tooltipValueGetter: this._tooltip,
                colId: 'type',
                suppressMenu: true,
                suppressMovable: true
            },
            {
                headerName: 'Alias',
                valueGetter: (params: ValueGetterParams) => params.data && params.data.aliases ? params.data.aliases.join(', ') : '',
                tooltipValueGetter: this._tooltip,
                colId: 'alias',
                suppressMenu: true,
                suppressMovable: true
            },
        ];
    columnDefsOrignal: Array<ColDef>;
    entityConfig: EntityConfig;
    entityId: string = null;
    entityList: EntityList;
    entityListWidth = 50;
    filters: object = {};
    isAdminUser = false;
    isComplianceUser = false;
    isRefresh = false;
    isShowBulkAction = false;
    gridFeedbackSubject$?: Subject<any> = new Subject<any>();
    gridOptions: GridOptions = {
        defaultColDef: {
            sortable: false,
            resizable: true,
        },
        cacheBlockSize: ResearchView.cacheBlockSize,
        suppressCellFocus: true,
        suppressColumnVirtualisation: true,
        enableBrowserTooltips: true,
        enableCellChangeFlash: true,
        paginationPageSize: DEFAULT_PAGE_SIZE,
        rowSelection: 'single',
        sortingOrder: ['desc', 'asc'],
        maintainColumnOrder: true,
        suppressContextMenu: true,
        suppressDragLeaveHidesColumns: true,
        suppressRowGroupHidesColumns: true,
        overlayNoRowsTemplate: '<span style=\'font-size: 20px;background-color:white;color:black\'>No Data Found</span>'
    };
    keyword = '';
    keywordSearchConfig: KeywordSearchConfig;
    inited = false;
    layoutConfig: ResearchLayoutConfig;
    loadingMore = false;
    noData = false;
    operationContextMenuConfig: ContextMenuConfig;
    page = 1;
    reloading = false;
    selectedIndex = 0;
    sorts: object = {};

    private _gridApi;
    private _subscriptions: Array<Subscription> = [];
    private _deleteColumnDef: ColDef =
        {
            headerName: 'Delete',
            minWidth: 105,
            width: 105,
            maxWidth: 105,
            colId: 'delete',
            cellRenderer: CustomActionColumnComponent,
            cellRendererParams: {
                feedbackSubject$: () => this.gridFeedbackSubject$
            },
            menuTabs: [],
            cellStyle: { 'text-align': 'center' },
        };

    constructor(
        private _route: ActivatedRoute,
        private _router: Router,
        private _store: Store<any>,
        private _storeQuerier: StoreQuerierService,
        private _toast: ToastService) {
        // get the status of isShowBulkAction
        const feedback = this._router.getCurrentNavigation()?.extras?.state;
        if (feedback) {
            this.isShowBulkAction = feedback['isShowBulkAction'];
            this.keyword = feedback['keyword'] ?? '';
        }
    }

    ngOnInit() {
        const currentUser = this._storeQuerier.getCurrentUser();
        if (currentUser) {
            this.isAdminUser = currentUser.credential.admin;
            this.isComplianceUser = currentUser.credential.compliance;
        }
        this.columnDefsOrignal = _lodash.cloneDeep(this.columnDefs);
        this.layoutConfig = new ResearchLayoutConfig(3, true, true);
        this.entityConfig = new EntityConfig(true);
        this.keywordSearchConfig = new KeywordSearchConfig();
        this._initOperationConfigContextMenu();
        this._subscriptions.push(
            this.entityConfig.feedbackSubject$.subscribe((action: EntityFeedbackAction) => this._entityFeedbackHandler(action))
        );
        this._subscriptions.push(
            this.keywordSearchConfig.events$.subscribe((action: any) => this._keywordSearchFeedbackHandler(action))
        );
        this._subscriptions.push(
            this.gridFeedbackSubject$.subscribe(feedback => this._handleGridColumnFeedback(feedback))
        );
        this._subscriptions.push(
            this._store.pipe(
                select(lastCustomActionSelector),
            ).subscribe(action => this._actionMessageHandler(action))
        );
    }

    ngOnDestroy() {
        this._subscriptions.forEach(subscription => {
            if (subscription && !subscription.closed) {
                subscription.unsubscribe();
            }
        });
    }

    clickRefreshEntitytList() {
        // If user click refresh,need to load data from page 1.
        this.page = 1;
        this._reload(this._getParams(), INIT, true);
    }

    onBodyScroll(e) {
        if (this.entityList && this.entityList.next && !this.loadingMore && e.api.gridBodyCtrl) {
            if (e.top + e.api.gridBodyCtrl.eBodyViewport.clientHeight + 1000 > e.api.gridBodyCtrl.eBodyViewport.scrollHeight) {
                this.loadingMore = true;
                this.page++;
                this._reload(this._getParams(), 'scroll');
            }
        }
    }

    onDisplayedColumnsChanged(event) {
        event.api.sizeColumnsToFit();
    }

    onExitClick() {
        this.isShowBulkAction = false;
        this.entityListWidth = 50;

        setTimeout(() => {
            if (this._gridApi) {
                this.columnDefs = [];
                this._gridApi.setColumnDefs(this.columnDefs);
                this.columnDefs = _lodash.cloneDeep(this.columnDefsOrignal);
                this._gridApi.setColumnDefs(this.columnDefs);
            }
        }, 300);

        this.page = 1;
        this.keyword = '';
        this._reload(this._getParams(), INIT);
    }

    /*AG Grid */
    onGridReady(params) {
        this._gridApi = params.api;
        // if before grid ready config ready, means component being reused as embedded
        // else means component self starts as url controller
        if (this.config) {
            this._initComponent(null);
        } else {
            const config = new EntityListConfig(
                smartSelector(null, EntitiesViewConstants.ReducerName)
            );
            const selfInitSubscription = config.feedbackSubject.subscribe((feedback: EntityListFeedBack) => {
                if (feedback.type === EntityListFeedbackType.VIEW_READY) {
                    this._reload(this._getParams(), INIT);
                    selfInitSubscription.unsubscribe();
                }
            });
            const currentPageInfo: any = {
                url: this._route.snapshot.url,
                name: 'Entities',
                type: 'ENTITIES',
                screenType: businessConstants.screenType.entity
            };
            this._store.dispatch({
                type: ScreenActionTypes.SET_VALUE,
                payload: currentPageInfo
            });
            this._initComponent(config);
        }
        this.config.feedbackSubject.next(new EntityListFeedBack(EntityListFeedbackType.VIEW_READY, null));

        if (this.isShowBulkAction) {
            this.keywordSearchConfig.actions$.next({ type: 'SET', payload: this.keyword, silentChange: true });
            this.entityListWidth = 100;
            const data = _lodash.cloneDeep(this.columnDefs);
            // add delete column to grid
            if (this.isAdminUser && this.isComplianceUser) {
                data.push(this._deleteColumnDef);
            }
            this.columnDefs = [];
            this._gridApi.setColumnDefs(this.columnDefs);
            this.columnDefs = data;
            this._gridApi.setColumnDefs(this.columnDefs);
        }


    }

    @HostListener('window:keydown', ['$event'])
    onKeydown(event) {
        // use up/down arrow keys to select through items
        if (event.keyCode === 40) { // down
            // get the index of last selected item
            let nextIndex = this.selectedIndex;
            if (++nextIndex < this.entityList.entities.length) {
                this._gridApi.forEachNode(function (node) {
                    if (node.rowIndex === nextIndex) {
                        node.setSelected(true);
                    } else {
                        node.setSelected(false);
                    }
                });
            }
        } else if (event.keyCode === 38) { // up
            // get the index of first selected item
            let nextIndex = this.selectedIndex;
            if (--nextIndex > -1) {
                this._gridApi.forEachNode(function (node) {
                    if (node.rowIndex === nextIndex) {
                        node.setSelected(true);
                    } else {
                        node.setSelected(false);
                    }
                });
            }
        }
    }

    @HostListener('window:resize', ['$event'])
    onResize(e) {
        if (!this._gridApi) {
            return;
        }
        this._gridApi.sizeColumnsToFit();
    }

    onRowSelected(event) {
        const item = event.api.getSelectedNodes()[0];
        if (item && item.data && item.data.id) {
            this._loadEntityDetail(item.data.id);
            this._onClicked(item.rowIndex);
        }
    }

    showContextMenu(e: MouseEvent) {
        this.operationContextMenuConfig.show$.next(new ContextMenuShowAction(e, ''));
    }

    private _actionMessageHandler(action) {
        if (action.type === EDIT_ENTITY_SUCCESS) {
            this._reload(this._getParams(), 'updated');
        }
    }

    private _onClicked(index) {
        this.selectedIndex = index;
        this.config.feedbackSubject.next(new EntityListFeedBack(EntityListFeedbackType.SELECTED, this.entityList[this.selectedIndex]));
    }

    private _entityFeedbackHandler(feedback: EntityFeedbackAction) {
        switch (feedback.type) {
            case EntityFeedbackTypes.ENTITY_LOADED:
                break;
            case EntityFeedbackTypes.ENTITY_UPDATED:
                this._reload(this._getParams(), 'updated');
                break;
        }
    }

    private _handleGridColumnFeedback(res) {
        if (res.type === GridCustomColumnFeedbackTypes.DELETE) {
            this._router.navigate(['entity', res.payload.data.id, 'associated-info'], { state: res.payload.data, queryParams: { source: '' + SourceType.ENTITY + '', keyword: '' + this.keyword + '' } });
        }
    }

    private _keywordSearchFeedbackHandler(feedback) {
        if (feedback && feedback.type === 'CHANGE' && feedback.payload) {
            this.page = 1;
            this.keyword = feedback.payload.state;
            this._reload(this._getParams(), 'keyword');
        }
    }

    private _getParams() {
        return {
            keyword: this.keyword,
            pageSize: DEFAULT_PAGE_SIZE,
            page: this.page
        };
    }

    private _initComponent(config: EntityListConfig) {
        if (!this.config) {
            this.config = config;
        }
        this._subscriptions.push(this.config.actionSubject.subscribe((action: EntityListAction) => {
            if (action.type === EntityListActionType.RELOAD) {
                if (action && action.payload) {
                    this.keyword = action.payload.keyword;
                }
                this._reload(action.payload, 'external');
            }
        }));

        this._subscriptions.push(this._store.select(this.config.rootSelector).subscribe((state) => {
            if (state) {
                this._onSubscribeEntityList(state);
            }
        }));
    }

    private _initOperationConfigContextMenu() {
        this.operationContextMenuConfig = new ContextMenuConfig();
        this.operationContextMenuConfig.stateful = false;
        this.operationContextMenuConfig.showItemIcon = false;
        this.operationContextMenuConfig.clickOnDiv = true;
        this.operationContextMenuConfig.items = [];
        this.operationContextMenuConfig.items.push(
            { id: '0', text: StringLiteralsPipe.translate('crm.bulk_actions') }
        );
        this.operationContextMenuConfig.events$.subscribe(event => this._onOperationMenuSelect(event));
    }

    private _loadEntityDetail(id: string) {
        this.entityId = id;
        setTimeout(() => {
            this.entityConfig.actionSubject$.next(new EntityAction(EntityActionTypes.SWITCH_ANOTHER_ENTITY, { id: id }));
        });
    }

    private _onOperationMenuSelect(event) {
        if (event.payload) {
            const id = event.payload.id;
            switch (id) {
                case '0':
                    this.isShowBulkAction = true;
                    this.entityListWidth = 100;
                    const data = _lodash.cloneDeep(this.columnDefs);
                    // add delete column to grid
                    if (this.isAdminUser && this.isComplianceUser) {
                        data.push(this._deleteColumnDef);
                    }
                    this.columnDefs = [];
                    this._gridApi.setColumnDefs(this.columnDefs);
                    this.columnDefs = data;
                    this._gridApi.setColumnDefs(this.columnDefs);
                    this._gridApi.setRowData(this.entityList.entities);
                    setTimeout(() => {
                        this._gridApi.sizeColumnsToFit();
                    });
                    break;
                default:
                    break;
            }
        }
    }

    private _onSubscribeEntityList(state) {
        this.noData = !(state && state.entityList && state.entityList.entities && state.entityList.entities.length > 0);
        this.entityList = state.entityList;
        this._postReload(state.params);
        this.reloading = false;
        if (this.isRefresh) {
            const notification: NotificationOptions = {
                message: StringLiteralsPipe.translate('crm.entity_list_is_refreshed'),
                style: NotificationStyles.Info

            };
            this._toast.notify(notification);
        }
    }

    private _postReload(params) {
        // Assign the datasource for ag-grid
        if (this.entityList && this.entityList.entities && this.entityList.entities.length > 0) {
            if (params) {
                // workflow = ['init' || 'keyword' || 'updated' || ‘scroll’ || 'external']
                if (params.workflow !== 'scroll') {
                    this._gridApi.setRowData(this.entityList.entities);
                    this.selectedIndex = 0;
                    const rowNode = this._gridApi.getRowNode(this.selectedIndex);
                    if (rowNode) {
                        rowNode.setSelected(true);
                    }

                    this._gridApi.setFocusedCell(this.selectedIndex, this.columnDefs[0].colId);
                } else if (params.workflow === 'scroll') {
                    if (this.loadingMore) {
                        this.loadingMore = false;
                        const startRowIndex = (params.page - 1) * DEFAULT_PAGE_SIZE;
                        const endRowIndex = params.page * DEFAULT_PAGE_SIZE;
                        this._gridApi.updateRowData({
                            add: this.entityList.entities.slice(startRowIndex, endRowIndex)
                        });
                    }
                }
                if (params.workflow === 'external' && params.keyword !== this.keyword) {
                    this.keywordSearchConfig.actions$.next({ type: 'SET', payload: params.keyword, silentChange: true });
                }
            }
            this._gridApi.sizeColumnsToFit();
        } else {
            this._gridApi.setRowData([]);
            this.entityId = null;
        }
        this.inited = true;
    }

    private _prepareAction(action: any, prefix: string, instanceId: string) {
        if (action && prefix && instanceId) {
            action[prefix] = instanceId;
        }
        return action;
    }

    private _reload(conditions, workflow, isRefresh = false) {
        this.isRefresh = isRefresh;

        if (this.reloading === false) {
            if (workflow !== 'scroll') {
                this.reloading = true;
            }
            const action = this._prepareAction({
                type: RELOAD_ENTITIES,
                payload: Object.assign({ workflow: workflow }, conditions)
            }, this.config.keywordField, this.config.instanceId);
            this._store.dispatch(action);
        }
    }

    private _tooltip(params) {
        return (params.value);
    }
}
